import React, { useEffect, useState } from "react";
import { companyCampusColleges, companySendInvite, companyCampusInvites } from "@/lib/campusApi";
import {
  GraduationCap, Loader2, X, Send, Users, Award, CheckCircle2, Clock,
  ExternalLink, RefreshCw, Sparkles,
} from "lucide-react";

const INVITE_STATUS = {
  pending: { cls: "bg-amber-100 text-amber-700", label: "Awaiting college" },
  accepted: { cls: "bg-emerald-100 text-emerald-700", label: "Live" },
  declined: { cls: "bg-stone-100 text-stone-500", label: "Declined" },
};

function InviteModal({ college, onClose, onSent }) {
  const [f, setF] = useState({ title: "", role: "", ctc: "", ctc_lpa: "", deadline: "", min_cgpa: "", min_wishire_score: "", message: "" });
  const [selDepts, setSelDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggleDept = (d) => setSelDepts((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.role.trim()) { setErr("Title and role are required."); return; }
    setBusy(true); setErr("");
    try {
      await companySendInvite({
        college_id: college.college_id,
        title: f.title.trim(), role: f.role.trim(),
        ctc: f.ctc.trim() || undefined,
        ctc_lpa: f.ctc_lpa !== "" ? Number(f.ctc_lpa) : undefined,
        deadline: f.deadline || undefined,
        min_cgpa: f.min_cgpa !== "" ? Number(f.min_cgpa) : undefined,
        min_wishire_score: f.min_wishire_score !== "" ? Number(f.min_wishire_score) : undefined,
        branches: selDepts.length ? selDepts : undefined,
        message: f.message.trim() || undefined,
      });
      onSent();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not send the invite."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg bg-white rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white">
          <div>
            <h2 className="font-display text-2xl">Invite {college.name}</h2>
            <p className="text-sm text-stone-500">{college.job_ready} job-ready of {college.students} students · avg score {college.avg_readiness}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-medium text-stone-500">Drive title *</span>
              <input value={f.title} onChange={set("title")} data-testid="inv-title" placeholder="SDE Campus Hiring 2026" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:outline-none focus:border-stone-900" /></label>
            <label className="block"><span className="text-xs font-medium text-stone-500">Role *</span>
              <input value={f.role} onChange={set("role")} data-testid="inv-role" placeholder="Software Engineer" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:outline-none focus:border-stone-900" /></label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block"><span className="text-xs font-medium text-stone-500">CTC</span>
              <input value={f.ctc} onChange={set("ctc")} placeholder="₹12 LPA" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm" /></label>
            <label className="block"><span className="text-xs font-medium text-stone-500">LPA (number)</span>
              <input type="number" step="0.1" value={f.ctc_lpa} onChange={set("ctc_lpa")} placeholder="12" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm font-mono" /></label>
            <label className="block"><span className="text-xs font-medium text-stone-500">Apply by</span>
              <input type="date" value={f.deadline} onChange={set("deadline")} className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm" /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-medium text-stone-500">Min CGPA</span>
              <input type="number" step="0.1" min="0" max="10" value={f.min_cgpa} onChange={set("min_cgpa")} placeholder="7.0" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm font-mono" /></label>
            <label className="block"><span className="text-xs font-medium text-stone-500">Min Wishire Score</span>
              <input type="number" min="0" max="100" value={f.min_wishire_score} onChange={set("min_wishire_score")} placeholder="60" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm font-mono" /></label>
          </div>
          {(college.departments || []).length > 0 && (
            <div>
              <span className="text-xs font-medium text-stone-500">Departments {selDepts.length > 0 && `· ${selDepts.length} selected`}</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {college.departments.map((d) => (
                  <button type="button" key={d} onClick={() => toggleDept(d)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${selDepts.includes(d) ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}>
                    {d}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-stone-400">None selected = all departments.</p>
            </div>
          )}
          <label className="block"><span className="text-xs font-medium text-stone-500">Message to the placement cell</span>
            <textarea value={f.message} onChange={set("message")} rows={2} placeholder="We're hiring 10+ freshers this quarter…" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm" /></label>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button type="submit" disabled={busy} data-testid="inv-send"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-stone-700 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <><Send size={15} /> Send hiring invite</>}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function CampusHiring() {
  const [colleges, setColleges] = useState(null);
  const [invites, setInvites] = useState(null);
  const [inviting, setInviting] = useState(null); // college being invited
  const [msg, setMsg] = useState(null);
  const load = () => {
    companyCampusColleges().then(({ data }) => setColleges(data)).catch(() => setColleges([]));
    companyCampusInvites().then(({ data }) => setInvites(data)).catch(() => setInvites([]));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="text-xs font-mono uppercase tracking-widest text-stone-400">Campus hiring</span>
          <h1 className="font-display text-3xl md:text-4xl mt-1">Hire fresh talent, scored before you meet them.</h1>
          <p className="mt-2 text-stone-600 max-w-2xl">Invite a college to run your drive — accepted invites go live on their campus instantly and your pipeline updates here in real time.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900"><RefreshCw size={14} /> Refresh</button>
      </div>

      {msg && <p className="mb-6 text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5" data-testid="ch-msg">{msg}</p>}
      {inviting && <InviteModal college={inviting} onClose={() => setInviting(null)} onSent={() => { setInviting(null); setMsg(`Invite sent to ${inviting.name} — you'll see the pipeline here the moment they accept.`); load(); }} />}

      {/* Live pipeline board */}
      <h2 className="font-display text-2xl mb-3">Your campus pipeline</h2>
      {invites === null ? <p className="text-stone-500 text-sm">Loading…</p> : invites.length === 0 ? (
        <div className="mb-10 rounded-3xl border border-dashed border-stone-300 bg-stone-50 p-8 text-center text-stone-500">
          No invites yet — pick a college below and send your first hiring invite.
        </div>
      ) : (
        <div className="mb-10 space-y-2.5" data-testid="ch-invites">
          {invites.map((inv) => {
            const st = INVITE_STATUS[inv.status] || INVITE_STATUS.pending;
            return (
              <div key={inv.invite_id} className="rounded-2xl border border-stone-200 bg-white px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-stone-900">
                      {inv.title} <span className="text-stone-400 font-normal">→ {inv.college_name}</span>
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                    </p>
                    <p className="text-xs text-stone-500">{inv.role}{inv.ctc ? ` · ${inv.ctc}` : ""}{inv.deadline ? ` · closes ${inv.deadline}` : ""}</p>
                  </div>
                  {inv.status === "accepted" && inv.share_token && (
                    <a href={`/d/${inv.share_token}`} target="_blank" rel="noreferrer"
                      className="text-xs font-medium px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 inline-flex items-center gap-1">
                      View shortlist <ExternalLink size={11} />
                    </a>
                  )}
                </div>
                {inv.status === "accepted" && inv.stats && (
                  <div className="mt-3 pt-3 border-t border-stone-100 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center" data-testid={`ch-stats-${inv.invite_id}`}>
                    {[["Applied", inv.stats.applicants], ["Shortlisted", inv.stats.shortlisted], ["Interview", inv.stats.interview],
                      ["Offers", inv.stats.offers], ["Placed", inv.stats.placed], ["Tasks", inv.stats.tasks]].map(([l, v]) => (
                      <div key={l} className="rounded-xl bg-stone-50 py-2">
                        <p className="font-display text-xl leading-none">{v}</p>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mt-1">{l}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* College directory */}
      <h2 className="font-display text-2xl mb-3">Colleges on Wishire</h2>
      {colleges === null ? <p className="text-stone-500 text-sm">Loading…</p> : colleges.length === 0 ? (
        <p className="text-stone-500 text-sm">No colleges with active batches yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="ch-directory">
          {colleges.map((c) => (
            <div key={c.college_id} className="rounded-3xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-3">
                {c.logo ? (
                  <img src={c.logo} alt={c.name} className="w-11 h-11 rounded-xl object-contain bg-stone-50 border border-stone-200" />
                ) : (
                  <span className="w-11 h-11 rounded-xl bg-stone-100 flex items-center justify-center text-stone-400"><GraduationCap size={20} /></span>
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-stone-900 truncate">{c.name}</p>
                  <p className="text-xs text-stone-500 truncate">{(c.departments || []).join(" · ") || "Departments not listed"}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-stone-50 py-2"><p className="font-display text-lg leading-none flex items-center justify-center gap-1"><Users size={13} className="text-stone-400" />{c.students}</p><p className="text-[10px] font-mono uppercase text-stone-400 mt-1">Students</p></div>
                <div className="rounded-xl bg-emerald-50 py-2"><p className="font-display text-lg leading-none flex items-center justify-center gap-1 text-emerald-700"><CheckCircle2 size={13} />{c.job_ready}</p><p className="text-[10px] font-mono uppercase text-stone-400 mt-1">Job-ready</p></div>
                <div className="rounded-xl bg-amber-50 py-2"><p className="font-display text-lg leading-none flex items-center justify-center gap-1 text-amber-700"><Award size={13} />{c.avg_readiness}</p><p className="text-[10px] font-mono uppercase text-stone-400 mt-1">Avg score</p></div>
              </div>
              <button onClick={() => setInviting(c)} data-testid={`ch-invite-${c.college_id}`}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-stone-700">
                <Sparkles size={14} /> Invite to hire
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
